import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import PropertyField from "@features/builder/NodeDrawer/NodeForm/PropertyField"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { Box, FormHelperText, Grid, IconButton, Tooltip } from "@mui/material"
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react"
import { useFormContext } from "react-hook-form"
import { GraphDetails } from "@features/builder/types"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { MissingScenarioFileCta } from "@features/builder/NodeDrawer/NodeForm/MissingScenarioFileCta"
import UnitField from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitField"
import NodeLinkField from "@components/NodeLink/NodeLinkField"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { Psychology } from "@mui/icons-material"
import { useGenerateLlmDescriptionMutation } from "@store/services/editorService"
import {
  clearLlmDescriptionTask,
  getLlmDescriptionByTaskId,
  llmDescriptionObserver,
} from "@features/builder/hooks/socketIOHooks"
import logger from "@utils/logger"
import { enqueueSnackbar } from "notistack"
import CommonHistoricMnemonicField from "@components/HistoricMnemonicField"
import { NodeTagsField } from "@features/builder/NodeDrawer/NodeForm/TagsField"
import { GraphTagsField } from "@features/builder/NodeDrawer/NodeForm/GraphTagsField"
import NodeExportableField from "@features/builder/NodeDrawer/NodeForm/NodeExportableField"

type NodePropertiesAccordionProps = PropsWithChildren & {
  id?: string
}
const NodePropertiesAccordion = ({
  id = "node-properties",
  children,
}: NodePropertiesAccordionProps) => {
  return (
    <NodeDrawerAccordion id={id} label="Node metadata">
      {children}
    </NodeDrawerAccordion>
  )
}

type HistoricMnemonicFieldProps = {
  nodeId: string
}

const HistoricMnemonicField = ({ nodeId }: HistoricMnemonicFieldProps) => {
  const form = useFormContext<NodeFormValues>()
  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const scenarioFilePath = graphData?.ScenarioSource

  const { getNodeValidation } = useNodesValidation()
  const errors = [getNodeValidation(nodeId, "HISTORIC_MNEMONIC")?.errors].flatMap(
    (err) => err ?? [],
  )

  return (
    <Grid xs={12} item>
      <CommonHistoricMnemonicField
        control={form.control}
        name="historic_mnemonic"
        label="Historic Mnemonic"
      />
      {errors.map((err, idx) => (
        <FormHelperText key={idx} error>
          - {err.msg}
        </FormHelperText>
      ))}
      {!scenarioFilePath && (
        <Box marginBottom={1}>
          <MissingScenarioFileCta />
        </Box>
      )}
    </Grid>
  )
}
HistoricMnemonicField.displayName = "HistoricMnemonicField"
NodePropertiesAccordion.HistoricMnemonicField = HistoricMnemonicField

NodePropertiesAccordion.UnitField = UnitField

NodePropertiesAccordion.ExportableField = NodeExportableField

const DescriptionField = () => {
  const form = useFormContext<NodeFormValues>()
  const { graphId } = useGraph()
  const fieldValue = form.watch("description")
  const nodeName = form.watch("label")
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null)
  const [triggerGenerateDescription, { isLoading: isFetching }] =
    useGenerateLlmDescriptionMutation()

  const subscribeToLlmDescription = useCallback((callback: () => void) => {
    const sub = llmDescriptionObserver.subscribe(callback)
    return () => sub.unsubscribe()
  }, [])

  const getLlmDescriptionSnapshot = useCallback(
    () => getLlmDescriptionByTaskId(pendingTaskId ?? undefined),
    [pendingTaskId],
  )

  const llmDescriptionUpdate = useSyncExternalStore(
    subscribeToLlmDescription,
    getLlmDescriptionSnapshot,
  )

  useEffect(() => {
    if (!pendingTaskId || !llmDescriptionUpdate) {
      return
    }

    if (llmDescriptionUpdate.status === "completed") {
      form.setValue("description", llmDescriptionUpdate.result ?? "", {
        shouldDirty: true,
      })
      clearLlmDescriptionTask(pendingTaskId)
      setPendingTaskId(null)
      return
    }

    if (llmDescriptionUpdate.status === "failed") {
      logger.error("llm-description websocket failed", {
        taskId: pendingTaskId,
        error: llmDescriptionUpdate.error,
      })

      enqueueSnackbar(
        <>
          LLM-DESCRIPTION failed <br />
          taskId: pendingTaskId, <br />
          error: llmDescriptionUpdate.error, <br />
        </>,
        { variant: "error" },
      )
      clearLlmDescriptionTask(pendingTaskId)
      setPendingTaskId(null)
    }
  }, [form, llmDescriptionUpdate, pendingTaskId])

  const handleGenerateDescription = async () => {
    if (!graphId || !nodeName?.trim()) {
      return
    }

    try {
      const response = await triggerGenerateDescription({
        graph_id: graphId,
        node_name: nodeName.trim(),
      }).unwrap()

      if (response.task_id) {
        setPendingTaskId(response.task_id)
      }
    } catch {
      logger.error("llm-description error")
    }
  }
  return (
    <Grid item>
      <Grid container item wrap="nowrap" gap={1} alignItems="center">
        <Grid item xs>
          <PropertyField
            control={form.control}
            label="Description"
            name="description"
            isLoading={isFetching || !!pendingTaskId}
            multiline
          />
        </Grid>
        <Grid item>
          <Tooltip title="Generate description">
            <span>
              <IconButton
                color="error"
                size="small"
                onClick={handleGenerateDescription}
                disabled={!!fieldValue || isFetching || form.formState.disabled || !!pendingTaskId}
              >
                <Psychology />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  )
}
DescriptionField.displayName = "DescriptionField"
NodePropertiesAccordion.DescriptionField = DescriptionField

const CommentsField = () => {
  const form = useFormContext<NodeFormValues>()
  return (
    <Grid item>
      <PropertyField
        control={form.control}
        label="Comments"
        name="comments"
        multiline
        minRows={2}
        maxRows={5}
      />
    </Grid>
  )
}
CommentsField.displayName = "CommentsField"
NodePropertiesAccordion.CommentsField = CommentsField

NodePropertiesAccordion.LinkField = NodeLinkField
NodePropertiesAccordion.NodeTagsField = NodeTagsField
NodePropertiesAccordion.GraphNodeTagsField = GraphTagsField

export default NodePropertiesAccordion
